import useFeatureFlags from "../../../Hooks/useFeatureFlags";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import useUserAccess from "../../../Hooks/useUserAccess";
import customerRoutes from "../../../Constants/Customer/customerRoutes";
import system from "../../../Constants/system";
import {FeatureFlags} from "../../../Utils/featureFlags";
import QuotationModal from "../../QuotationModal";
import Modal from "../../Modal";
import {UserRoles} from "../../../Constants/userRoles";
import Tooltip, {TooltipContent, TooltipTrigger} from "../../Overlays/Tooltip";
import SkynetIcon from "../../Fundamentals/SkynetIcon";
import UserSearchNew from "../../Search/UserSearchNew";
import {FormInput} from "../../FormComponents/Input/Input";
import TextInput from "../../FormComponents/Input/TextInput";
import Loader from "../../Loader";
import React, {useEffect, useState} from "react";
import useModal from "../../../Hooks/useModal";
import {Dropdown} from "../../FormComponents/Dropdown";

export default function ConfirmOrderModal({ commonActions, orderActions, isQuotation, order, quotationCustomerForm, isAboveCreditLimit, customer, orderForm, sourceSystem, customerActions }) {

    const { isFeatureEnabled } = useFeatureFlags();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { hasReadWriteAccess } = useUserAccess();
    const { closeModal } = useModal();

    useEffect(() => {
        if (!orderForm.data.orderType) {
            onChange(orderForm.id, 'orderType', 'ordinary');
        }
    }, []);

    const onChange = (form, field, value) => {
        commonActions.changeMultiFormField(form, field, value)
    }

    useEffect(() => {
        if (orderForm.data.orderType !== 'other' && orderForm.data.salesPersonEmail) {
            onChange(orderForm.id, 'salesPersonEmail', null);
        }
    }, [orderForm.data.orderType, orderForm.data.salesPersonEmail, onChange, orderForm.id]);

    const onSendApprovalRequest = () => {
        orderActions.sendApprovalRequest(
            order.orderId,
            orderForm,
            () => navigate(customerRoutes.customer(customer.pathId))
        );
    };

    const callback = (newOrderId) => {
        switch (sourceSystem) {
            case system.Vb:
                customerActions.getCustomerVBProducts(customer.vbCustomer.customerNumber);
                break;
            case system.Voss:
                customerActions.searchSubscriptions(customer.vossCustomer.id);
                break;
            case system.Blikk:
                customerActions.getBlikkProducts(order.customerNumber);
                break;
        }
        closeModal();
        navigate(customerRoutes.customer(customer.pathId, customerRoutes.orderConfirmationEmail(newOrderId)));
    }

    const onComplete = () => {
        if (order.customerNumber) {
            orderActions.completeByCustomerNumber(
                order.orderId,
                order.customerNumber,
                orderForm,
                callback);
        }
        else
        {
            orderActions.completeByCustomerId(
                order.orderId,
                order.customerId,
                orderForm,
                callback)
        }
    };

    const copyContactInfo = () => {
        commonActions.changeMultiFormField(orderForm.id, 'yourReference', customer.contactPerson?.fullName)
        commonActions.changeMultiFormField(orderForm.id, 'emailAddress', customer.contactPerson.email)
    }

    const modalProps = isQuotation ? {
        onChange: onChange,
        onClose: closeModal,
        onLoad: () => orderActions.startQuotationModal(order, quotationCustomerForm, true),
        onSave: (form) => orderActions.sendQuotation(form, closeModal, true,  () => navigate(customerRoutes.customer(customer.pathId)))
    } : {
        open: true,
        stateful: true,
        showButtons: false,
        title: t("confirmordermodal.confirm"),
        onCancelClick: closeModal,
    };
    
    const orderTypeOptions = [
        { id: 'ordinary', label: t('confirmordermodal.ordinary') },
        {
            id: 'other',
            label: t('confirmordermodal.orderforother'),
            disabled: !hasReadWriteAccess(UserRoles.OrderForOther)
        },
        { id: 'partner', label: t('confirmordermodal.partner') },
        { id: 'web', label: t('confirmordermodal.web') },
        { id: 'onlineSales', label: t('confirmordermodal.onlineorder') }
    ];

    return <>
        {isQuotation
            ? <QuotationModal {...modalProps} />
            : <Modal {...modalProps}>
                <div className="">
                    <div className='mb-16'>
                        <FormInput label={t("confirmordermodal.name")} required={true} onChange={(value) => onChange(orderForm.id, 'yourReference', value)}
                                   value={orderForm.data.yourReference} error={orderForm.fieldErrorFlags && orderForm.fieldErrorFlags["yourReference"]}/>
                    </div>
                    <div className='mb-16'>
                        <FormInput label={t("confirmordermodal.confirmationemail")} required={true}
                                   value={orderForm.data.emailAddress} onChange={(value) => onChange(orderForm.id, 'emailAddress', value)} error={orderForm.fieldErrorFlags && orderForm.fieldErrorFlags["emailAddress"]}/>
                    </div>
                    <div className='d-flex justify-content-between'>
                        <div>
                            {customer.email && <TextInput label={t("confirmordermodal.invoiceemail")} readonly value={customer.email} disabled />}
                        </div>
                        <div>
                            <button type="button" className="btn btn-sm mb-0" onClick={() => copyContactInfo()}>{t("confirmordermodal.usecustomerinfo")}</button>
                        </div>
                    </div>
                    <div className='d-flex justify-content-between align-items-end mb-16'>
                        <div className="form-group flex-grow-1 col-6 pl-0">
                            <Dropdown options={orderTypeOptions}
                                      value={orderForm.data.orderType}
                                      onChange={(value) => onChange(orderForm.id, 'orderType', value)}
                                      property="label"
                                      label={t('confirmordermodal.orderType')}
                                      disabledOptionKey="disabled"></Dropdown>
                        </div>
                        {hasReadWriteAccess(UserRoles.OrderForOther) && orderForm.data.orderType === "other" &&
                            <div>
                                <Tooltip>
                                    <TooltipContent>
                                        {t("confirmordermodal.orderforothertooltip")}
                                    </TooltipContent>
                                </Tooltip>
                                <UserSearchNew defaultSelected={orderForm.data.salesPersonEmail} onChange={(salesPerson) => onChange(orderForm.id, 'salesPersonEmail', salesPerson.email)}/>
                            </div>
                        }
                    </div>
                    <div className='d-flex justify-content-end gap-8'>
                        <button type="button" className="btn btn-sm mb-0" onClick={closeModal}>{t("confirmordermodal.cancel")}</button>
                        <button type="button" className="btn btn-primary btn-sm mb-0" onClick={() => isAboveCreditLimit ? onSendApprovalRequest() : onComplete()}>
                            {isAboveCreditLimit ? t('ordercartcontainer.requestapproval') : t('ordercartcontainer.completeorder')}
                        </button>
                    </div>
                </div>
                <Loader />
            </Modal>
        }
    </>
};